import React, { useRef } from "react";
import { createPortal } from "react-dom";
import { X, AlertTriangle } from "lucide-react";
import { cn } from "../../../lib/cn";
import { useOverlay, usePresence, useNavbarOffset, useSidebarOffset } from "./overlayBehavior";

/**
 * Modal — ui-v2.5. Diálogo centrado. Especificación: catálogo · sección Modal.
 * Tamaños sm 384 · md 512 · lg 672 · xl 1024. Cuerpo con scroll propio (max-height:92vh).
 * Tono `danger`: icono en círculo rojo, el velo NO cierra al clic, y el foco inicial va al PRIMER
 * enfocable (por convención, el botón Cancelar), NUNCA al destructivo.
 *
 * CONVENCIÓN destructivo: en el `footer` de un Modal danger, coloca Cancelar PRIMERO y el botón
 * destructivo el ÚLTIMO. Así el foco inicial (primer enfocable) cae en Cancelar y Enter no dispara
 * la acción destructiva. El destructivo debe ser sólido (#dc2626) y nunca la acción por defecto.
 *
 * <Modal open onClose size="sm|md|lg|xl" title subtitle footer tone="default|danger">{cuerpo}</Modal>
 */

const SIZE = {
  sm: "max-w-[384px]",
  md: "max-w-[512px]",
  lg: "max-w-[672px]",
  xl: "max-w-[1024px]",
};

export function Modal({ open, onClose, size = "md", title, subtitle, footer, tone = "default", children, className }) {
  const panelRef = useRef(null);
  const { mounted, shown } = usePresence(open, 220);
  const danger = tone === "danger";
  const navOffset = useNavbarOffset();
  const sidebarOffset = useSidebarOffset();
  useOverlay({ active: open && mounted, onClose, panelRef });

  if (!mounted) return null;

/**
 * PORTAL · el overlay se monta en `document.body`, no donde lo escriba el consumidor.
 *
 * Sin portal, hereda el arbol en el que lo montas — y eso tiene una consecuencia que no se ve:
 * **dentro de un `<form>`, un `<form>` anidado es HTML invalido**, asi que el boton `type="submit"`
 * del overlay acaba enviando el formulario EXTERIOR por el camino nativo. La pagina se recarga, el
 * manejador del overlay nunca corre, y no hay ningun error.
 *
 * El diagnostico se ve en la URL: **una interrogacion vacia al final** (`/editarPersona/2?`) es la
 * firma de una navegacion nativa por GET.
 *
 * Lo que el portal NO cambia: `useNavbarOffset` y `useSidebarOffset` miden nodos GLOBALES
 * (`[data-testid="app-navbar"]`, `#mySidebar`), no ancestros del overlay; el bloqueo de scroll, la
 * pila de Escape y el retorno de foco trabajan sobre `document`; y el foco atrapado, sobre
 * `panelRef`. Todo eso es indiferente a donde cuelgue el nodo.
 *
 * Lo que SI podria haber cambiado, y se midio antes y despues: un `position: fixed` deja de
 * referirse al viewport si algun ANCESTRO tiene transform/filter/will-change. Ninguno de los
 * overlays medidos lo tenia, y las posiciones salen identicas.
 */
  return createPortal(
    // Contenedor bajo el NavBar (top:navOffset) y a la derecha del sidebar (left:sidebarOffset): el velo
    // y el panel respetan ambos, así el centrado (justify-center) ocurre en el ÁREA DE CONTENIDO y un
    // Modal ancho (xl) no queda tapado por el sidebar. Coherente con el Drawer.
    <div className="fixed bottom-0 right-0 z-50 flex items-center justify-center p-4" style={{ top: navOffset, left: sidebarOffset }}>
      <div
        className={cn("absolute inset-0 bg-black/40 transition-opacity duration-200", shown ? "opacity-100" : "opacity-0")}
        onClick={danger ? undefined : onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        style={{ transitionDuration: "220ms" }}
        className={cn(
          "relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-v25-lg bg-white shadow-v25-modal transition-all",
          SIZE[size] || SIZE.md,
          shown ? "scale-100 opacity-100" : "scale-[0.96] opacity-0",
          className
        )}
      >
        {danger ? (
          <>
            <div className="p-5">
              <div className="mb-3 grid h-10 w-10 place-items-center rounded-v25-full bg-v25-expired-bg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              {title && <h3 className={cn("text-sm font-semibold text-v25-ink-900", subtitle ? "mb-0.5" : "mb-1")}>{title}</h3>}
              {/* El `subtitle` FALTABA en esta rama: solo lo pintaba la variante por defecto, así que
                  en un modal `danger` se descartaba en silencio. Ocho confirmaciones destructivas del
                  proyecto (cancelar solicitud, borrar datos del candidato, desactivar área/recurso/
                  puesto/perfil/regla, eliminar definitivamente) preguntaban "¿seguro?" SIN decir sobre
                  qué registro actuaban — que es justo el dato que evita borrar el equivocado. */}
              {subtitle && <p className="mb-2 text-[11px] text-v25-ink-500">{subtitle}</p>}
              <div className="text-xs text-v25-ink-500">{children}</div>
            </div>
            {footer && (
              <div className="flex gap-2 border-t border-v25-surface-raised bg-v25-surface-sunken px-5 py-4">{footer}</div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-start justify-between border-b border-v25-surface-raised px-6 py-4">
              <div className="min-w-0">
                {title && <h3 className="text-base font-semibold text-v25-ink-900">{title}</h3>}
                {subtitle && <p className="mt-0.5 text-[11px] text-v25-ink-500">{subtitle}</p>}
              </div>
              <button type="button" onClick={onClose} aria-label="Cerrar" data-overlay-close="true" className="shrink-0 text-v25-ink-400 transition-colors hover:text-v25-ink-700 focus-visible:outline-none focus-visible:shadow-v25-focus">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
            {footer && (
              <div className="flex items-center justify-end gap-2 border-t border-v25-surface-raised bg-v25-surface-sunken px-6 py-4">{footer}</div>
            )}
          </>
        )}
      </div>
    </div>
  , document.body);
}
